/**
 * Turns viewer data into the flat field maps `ruleEngine` evaluates.
 *
 * The rule engine deliberately knows nothing about Cornerstone stacks or JPA
 * entities — it reads `{ fieldName: value }`. This module is the viewer's half
 * of that contract, and the only place that knows how a registry field name maps
 * onto a loaded study.
 *
 * ## Normalised study shape
 *
 * Callers pass a study in this shape (see `fromViewerStudyData` for the adapter
 * from the basic viewer's own structures):
 *
 * ```
 * {
 *   description, modalities: [..],
 *   series: [{ modality, ordinal, isImage, description, number, bodyPart,
 *              sopClassUID, transferSyntaxUID, instancesCount,
 *              instances: [{ ordinal, number, sopClassUID, transferSyntaxUID, tags }] }]
 * }
 * ```
 *
 * `ordinal` is the 1-based position the Advance Viewer counts from — of a series
 * among the study's image series, and of an instance within its series — and is
 * what `series_number` and `instance_number` mean. `number` is the DICOM
 * SeriesNumber / InstanceNumber, which only the tag fields expose. `isImage` is
 * false for presentation states, key objects and SRs: the Advance Viewer carries
 * those as attachments rather than series, so they take no ordinal, are never
 * hung, and do not count towards the study-scope aggregates. Adapters set all
 * three.
 *
 * ## Coverage
 *
 * Fields the basic viewer cannot source are simply absent from the context, and
 * the engine treats absent as empty — so a `contain` rule fails and a
 * `not_contain` rule passes, which is the safe direction: an unsourceable
 * condition never falsely claims a match. {@link unsupportedFields} reports
 * which fields a protocol relies on that we cannot answer, so callers can warn
 * rather than hang something subtly wrong.
 */

import { ALL_KNOWN_FIELDS } from "./fieldRegistry";
import { WALK_DIRECTIVE_FIELDS } from "./ruleEngine";

/** DICOM tag keys the instance scope can read once an image is loaded. */
const INSTANCE_TAG_FIELDS = {
  "instance.ImageLaterality[0020,0062]": "imageLaterality",
  "instance.Laterality[0020,006X]": "laterality",
  "instance.Laterality[All]": "laterality",
  "instance.ImageType[0008,0008]": "imageType",
  "instance.PatientOrientation[0020,0020]": "patientOrientation",
  "instance.ViewPosition[0018,5101]": "viewPosition",
  "instance.ViewPosition[ANY]": "viewPosition",
  "instance.BodyPartExamined[0018,0015]": "bodyPartExamined",
  "instance.ProtocolName[0018,1030]": "protocolName",
  "instance.SamplesPerPixel[0028,0002)]": "samplesPerPixel",
  "instance.ContrastBolusAgent[0018,0010]": "contrastBolusAgent",
  "instance.RotationDirection[0018,1140]": "rotationDirection",
};

/** Series-scope fields that read the same tags off the series' first instance. */
const SERIES_TAG_FIELDS = {
  "series.ImageLaterality[0020,0062]": "imageLaterality",
  "series.Laterality[0020,006X]": "laterality",
  "series.Laterality[All]": "laterality",
  "series.ImageType[0008,0008]": "imageType",
  "series.PatientOrientation[0020,0020]": "patientOrientation",
  "series.ViewPosition[0018,5101]": "viewPosition",
  "series.ViewPosition[ANY]": "viewPosition",
  "series.BodyPartExamined[0018,0015]": "bodyPartExamined",
  "series.ProtocolName[0018,1030]": "protocolName",
  "series.ContrastBolusAgent[0018,0010]": "contrastBolusAgent",
  "series.RotationDirection[0018,1140]": "rotationDirection",
};

/** Everything this module can answer. Anything else is reported unsupported. */
export const SUPPORTED_FIELDS = new Set([
  // study / session
  "main_study_modality",
  "modalities_in_study",
  "study_description",
  "study_instances_count",
  "series_count",
  "open_studies_count",
  "patient_studies_count",
  "patient_studies_modality_count",
  "open_studies_modality_count",
  // series
  "series_modality",
  "series_description",
  "series_number",
  "series_instances_count",
  "series_SOP_class_UID",
  "series_transfer_syntax_UID",
  "series.SeriesNumber[0020,0011]",
  "matched_series_number",
  // instance
  "instance_number",
  "instance_SOP_class_UID",
  "instance_transfer_syntax_UID",
  "number_of_frames",
  "matched_instance_number",
  "sop_instance_UID",
  // prior — see buildPriorContext. The `other` vocabulary
  // (modalities_in_open_study, open_study_description) stays unsupported: the
  // basic viewer opens one study at a time, so there are no "other" studies.
  "modalities_in_patient_study",
  "patient_study_description",
  "scroll_step",
  "study_date_filter",
  ...Object.keys(SERIES_TAG_FIELDS),
  ...Object.keys(INSTANCE_TAG_FIELDS),
]);

const distinct = (values) => Array.from(new Set(values.filter((v) => v !== null && v !== undefined && v !== "")));

/** Modalities the Advance Viewer carries as attachments rather than hangable series. */
const NON_IMAGE_MODALITIES = new Set(["PR", "SR", "KO", "DOC"]);

/** A series a viewport can hang. Absent `isImage` reads as one, so a hand-built study still matches. */
const isImageSeries = (series) => series?.isImage !== false;

/* ---------------------------------------------------------------- study */

/**
 * Study- and session-scope fields. Used for `groupConditions` and
 * `studyConditions`, which is why both scopes are in one map — the two
 * vocabularies overlap heavily and neither has anything the other lacks here.
 */
export function buildStudyContext(study, session = {}) {
  // Image series only: the Advance Viewer's study-scope aggregates count the
  // series it hangs, and presentation states / key objects / SRs are not among
  // them — nor is their modality part of the study's.
  const series = (study?.series ?? []).filter(isImageSeries);
  const modalities = distinct(study?.modalities ?? series.map((s) => s.modality));

  return {
    main_study_modality: modalities[0] ?? null,
    modalities_in_study: modalities,
    study_description: study?.description ?? null,
    series_count: series.length,
    study_instances_count: series.reduce((total, s) => total + (s.instancesCount ?? s.instances?.length ?? 0), 0),
    // Session-level counts the viewer supplies; default to this one study.
    open_studies_count: session.openStudiesCount ?? 1,
    patient_studies_count: session.patientStudiesCount ?? 1,
    patient_studies_modality_count: session.patientStudiesModalityCount ?? modalities.length,
    open_studies_modality_count: session.openStudiesModalityCount ?? modalities.length,
    // Present so a study-scoped rule on these does not silently see nothing.
    series_description: distinct(series.map((s) => s.description)),
    series_SOP_class_UID: distinct(series.map((s) => s.sopClassUID)),
    series_transfer_syntax_UID: distinct(series.map((s) => s.transferSyntaxUID)),
    ...tagContext(SERIES_TAG_FIELDS, mergeSeriesTags(series)),
  };
}

/** Union of every series' tags, so a study-scoped laterality rule can match. */
function mergeSeriesTags(series) {
  const merged = {};
  series.forEach((s) => {
    Object.entries(s.tags ?? s.instances?.[0]?.tags ?? {}).forEach(([key, value]) => {
      merged[key] = merged[key] ? [].concat(merged[key], value) : value;
    });
  });
  return merged;
}

/* ---------------------------------------------------------------- prior */

/**
 * A date the rule engine can compare. `study_date_filter` is evaluated with
 * `Date.parse`, and the API sends `studyDate` as epoch milliseconds (Jackson's
 * default for `java.util.Date`; nothing configures otherwise), which
 * `Date.parse` cannot read. `studyDateDisplay` is no better — "12-Aug-26 19:22"
 * is not a format any engine parses reliably. So both are normalised to ISO here.
 */
function toDateValue(value) {
  if (value === null || value === undefined || value === "") return null;
  const millis = typeof value === "number" ? value : Number(value);
  if (!Number.isNaN(millis) && String(value).trim() !== "") return new Date(millis).toISOString();
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : new Date(parsed).toISOString();
}

/**
 * Prior-scope fields — the vocabulary `priorStudyConditions` draws from.
 *
 * Built per candidate study: each entry in `priorStudyConditions` identifies ONE
 * prior, which is why a viewport addresses it by number through
 * `viewport.priorStudy`. So `modalities_in_patient_study` is that candidate's
 * own modalities, not the union across the patient's studies.
 *
 * That reading is UNVERIFIED against the Advance Viewer — MedDream evaluates
 * server-side. It is the only one consistent with `viewport.priorStudy` being an
 * index, with the slot being an array, and with the patient-wide aggregates
 * already existing in study scope (`patient_studies_modality_count`). If it
 * turns out MedDream aggregates patient-wide, this function is the only place
 * that has to change: pass the union of every patient study's modalities.
 *
 * `offset` is the candidate's 1-based distance from the current study, newest
 * first, which is what `scroll_step` counts.
 */
export function buildPriorContext(study, { offset = null } = {}) {
  const series = study?.series ?? [];
  return {
    modalities_in_patient_study: distinct(study?.modalities ?? series.map((s) => s.modality)),
    patient_study_description: study?.description ?? null,
    scroll_step: offset,
    study_date_filter: toDateValue(study?.studyDate),
  };
}

/* --------------------------------------------------------------- series */

/**
 * Series-scope fields, layered over the study context so a viewport condition
 * can mix scopes — which the reference export does.
 *
 * `matchedIndex` is the 1-based position of this series among those already
 * matched by earlier viewports, which is what `matched_series_number` means.
 */
export function buildSeriesContext(series, { studyContext = {}, matchedIndex = null } = {}) {
  const tags = series?.tags ?? series?.instances?.[0]?.tags ?? {};
  return {
    ...studyContext,
    series_modality: series?.modality ?? null,
    series_description: series?.description ?? null,
    // The Advance Viewer resolves `series_number` as the series' 1-based position
    // in the study (`series.getIndex() + 1`, in its own condition evaluator), not
    // as SeriesNumber (0020,0011) — that value is reachable through the tag field
    // below. Reading it as the DICOM number silently drops every protocol that
    // selects series by number, since real SeriesNumbers are rarely 1, 2, 3.
    series_number: series?.ordinal ?? null,
    "series.SeriesNumber[0020,0011]": series?.number ?? null,
    series_instances_count: series?.instancesCount ?? series?.instances?.length ?? 0,
    series_SOP_class_UID: series?.sopClassUID ?? null,
    series_transfer_syntax_UID: series?.transferSyntaxUID ?? null,
    matched_series_number: matchedIndex,
    ...tagContext(SERIES_TAG_FIELDS, tags),
  };
}

/* ------------------------------------------------------------- instance */

/**
 * Instance-scope fields, layered over the series context.
 *
 * `tags` are the DICOM values read from the loaded image; the caller resolves
 * them lazily, since loading every instance to answer a condition that may not
 * reference tags at all would be wasteful.
 */
export function buildInstanceContext(instance, { seriesContext = {}, tags = null, matchedIndex = null } = {}) {
  const resolved = tags ?? instance?.tags ?? {};
  return {
    ...seriesContext,
    // Positional too, as the Advance Viewer reads it (`instance.getIndex() + 1`);
    // InstanceNumber (0020,0013) is not a field any condition can name.
    instance_number: instance?.ordinal ?? null,
    instance_SOP_class_UID: instance?.sopClassUID ?? null,
    instance_transfer_syntax_UID: instance?.transferSyntaxUID ?? null,
    sop_instance_UID: instance?.sopInstanceUID ?? null,
    number_of_frames: instance?.numberOfFrames ?? null,
    matched_instance_number: matchedIndex,
    ...tagContext(INSTANCE_TAG_FIELDS, resolved),
  };
}

function tagContext(fieldMap, tags) {
  const context = {};
  Object.entries(fieldMap).forEach(([field, tagKey]) => {
    if (tags && tags[tagKey] !== undefined) context[field] = tags[tagKey];
  });
  return context;
}

/* ------------------------------------------------------------- adapter */

/**
 * Basic viewer's `studyData` -> the normalised study the planner reads.
 *
 * The viewer calls a series a "stack" and numbers it `stackId`; images live on
 * `stack.images`. Keeping that vocabulary out of the planner is the point of
 * this function — it is the only place the two shapes meet.
 *
 * `seriesIndex` in a plan is an index into `study.series`, which is the same
 * index as `studyData.stacks`, so the caller can map an assignment straight back
 * onto a stack. That is why a non-image stack keeps its place here rather than
 * being filtered out: it is marked `isImage: false` instead, and the planner
 * skips it.
 */

export function fromViewerStudyData(studyData, { studyDescription = null, studyModality = null } = {}) {
  const stacks = studyData?.stacks ?? [];

  // The stack's own DICOM modality. `stack.modality` holds the study-level value
  // for image series (only PR / SR / KO keep theirs), so it cannot tell a PET
  // series from the CT it is fused with — which a `series_modality` rule must.
  const seriesModalityOf = (stack) => stack?.seriesModality ?? stack?.modality ?? null;
  const isImageStack = (stack) => !NON_IMAGE_MODALITIES.has(seriesModalityOf(stack));

  // Ordinals count image series only, because that is the list the Advance
  // Viewer numbers: `series_number = 1` means the first series a radiologist
  // sees, not the first entry of this array — which for a study carrying a
  // presentation state is that presentation state.
  let ordinal = 0;

  const series = stacks.map((stack) => {
    const isImage = isImageStack(stack);
    if (isImage) ordinal += 1;

    return {
      modality: seriesModalityOf(stack),
      isImage,
      ordinal: isImage ? ordinal : null,
      description: stack.seriesDescription ?? null,
      // The viewer stores SeriesNumber as stackId.
      number: stack.stackId ?? null,
      bodyPart: stack.bodyPart ?? null,
      // A stack carries no SOP class of its own — nothing in the viewer ever
      // reads one onto it — so the series' comes from its first instance, which
      // is where the API sends it (ImagesDto.sopClassUID). Left null, every
      // `series_SOP_class_UID` rule failed as an absent field, and the shared MG
      // group — which gates its twelve protocols on the Digital Mammography SOP
      // class — matched nothing: a mammogram the Advance Viewer hangs from that
      // group was offered no MG protocol at all here. A series is one SOP class
      // in practice, so its first instance answers for it.
      sopClassUID: stack.sopClassUID ?? stack.images?.[0]?.sopClassUID ?? null,
      transferSyntaxUID: stack.transferSyntaxUID ?? null,
      instancesCount: stack.images?.length ?? stack.imageIds?.length ?? 0,
      instances: (stack.images ?? []).map((image, index) => ({
        ordinal: index + 1,
        number: image.instanceNumber ?? index + 1,
        sopClassUID: image.sopClassUID ?? null,
        sopInstanceUID: image.sopInstanceUID ?? null,
        transferSyntaxUID: image.transferSyntaxUID ?? null,
        numberOfFrames: image.numberOfFrames ?? null,
      })),
    };
  });

  return {
    description: studyDescription ?? stacks[0]?.studyDescription ?? null,
    // The study's own modality leads, so `main_study_modality` is the study's and
    // not whichever series sorts first; the rest is every image series' own,
    // which is what `modalities_in_study` reads. Where the caller does not know
    // the study's modality, an image stack's `modality` carries it.
    // `||`, not `??`: the viewer holds "" for the modality until the study lands.
    modalities: distinct([
      studyModality || stacks.find(isImageStack)?.modality || null,
      ...series.filter(isImageSeries).map((s) => s.modality),
    ]),
    series,
  };
}

/**
 * Adapter from the API's StudyAPIDto — the shape `/patient/get` returns for the
 * current study and each of the patient's others.
 *
 * Prior selection runs on that response, before the viewer has built any stacks,
 * so it cannot go through `fromViewerStudyData`. The DTO carries less than a
 * loaded study does: no per-series SOP class / transfer syntax and no DICOM
 * tags. Those fields stay absent rather than wrong — see `DTO_MATCHABLE_FIELDS`
 * in priorSelection.js, which refuses to filter at all when a document depends
 * on them.
 */
export function fromStudyDto(dto) {
  let ordinal = 0;

  const series = (dto?.seriesList ?? []).map((s) => {
    // The API nests modality as an entity; the viewer's own data has it flat.
    const modality = s.modality?.name ?? s.modality ?? null;
    const isImage = !NON_IMAGE_MODALITIES.has(modality);
    if (isImage) ordinal += 1;

    return {
      modality,
      isImage,
      ordinal: isImage ? ordinal : null,
      description: s.seriesDescription ?? null,
      number: s.seriesNumber ?? null,
      bodyPart: s.bodyPart ?? null,
      instancesCount: s.imageList?.length ?? 0,
    };
  });

  return {
    description: dto?.studyDescription ?? null,
    // The study's own modality first: a study whose series list has not loaded
    // still knows what it is.
    modalities: distinct([dto?.modality, ...series.filter(isImageSeries).map((s) => s.modality)]),
    series,
    studyDate: dto?.studyDate ?? dto?.studyDateDisplay ?? null,
  };
}


/* ------------------------------------------------------------ coverage */

/** Every field a condition tree references. */
export function fieldsUsed(node, into = new Set()) {
  if (!node) return into;
  if (Array.isArray(node)) {
    node.forEach((entry) => fieldsUsed(entry, into));
    return into;
  }
  if (typeof node !== "object") return into;
  if (Array.isArray(node.rules)) return fieldsUsed(node.rules, into);
  if (node.field) into.add(node.field);
  return into;
}

/**
 * Fields a protocol relies on that the basic viewer cannot source. Non-empty
 * means the hang may differ from what the Advance Viewer would produce, which is
 * worth telling the user rather than silently mis-hanging.
 */
export function unsupportedFields(category) {
  const used = new Set();
  fieldsUsed(category?.studyConditions, used);
  (category?.priorStudyConditions ?? []).forEach((g) => fieldsUsed(g, used));
  (category?.otherStudyConditions ?? []).forEach((g) => fieldsUsed(g, used));
  (category?.panelsLayout?.panels ?? []).forEach((panel) => {
    (panel.layout?.viewports ?? []).forEach((viewport) => {
      ["seriesConditions", "petSeriesConditions", "instanceConditions", "koInstance", "autoApplyPR"].forEach(
        (slot) => fieldsUsed(viewport[slot], used)
      );
    });
  });
  // A walk directive is not an unanswered field: both engines skip it by design,
  // so a protocol carrying one hangs exactly as intended. Reporting it here would
  // have every shared MG protocol claim the Advance Viewer hangs it differently.
  return [...used].filter(
    (field) =>
      ALL_KNOWN_FIELDS.includes(field) && !SUPPORTED_FIELDS.has(field) && !WALK_DIRECTIVE_FIELDS.has(field)
  );
}
